import {Component, computed, effect, Injectable, Input, OnInit, Signal, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormGroup, ReactiveFormsModule} from '@angular/forms';
import {CategoryService, PrimaryCategoryChildrenListResponse, ResourceSearchSchema} from '@middleware';
import {PrimaryCategory, SecondaryCategory, TertiaryCategory} from '@models/category';
import {CustomFieldTypes} from '@models/form-field';
import {FormlyFieldConfig, FormlyFormOptions, FormlyModule} from '@ngx-formly/core';
import {addChildrenToCats} from '@utilities/categories';
import {map, Observable, tap} from 'rxjs';
import {Filter, FilterSelection, FilterVisibility} from '../filter';

@Component({
  template: `<form [formGroup]="form"
    ><formly-form [model]="model" [fields]="fields" [options]="options" [form]="form"></formly-form
  ></form>`,
  imports: [ReactiveFormsModule, FormlyModule],
})
class CategoryView implements OnInit {
  @Input() filter: CategoryFilter;

  form = new FormGroup({});
  model: unknown = {};
  options: FormlyFormOptions = {};
  fields: FormlyFieldConfig[];

  constructor() {
    effect(() => {
      // Watch for changes to selected values (triggered by URL query string update)
      const catIds = this.filter?.selectedValues();

      if (Array.isArray(catIds) && this.fields?.[0]?.formControl) {
        // Use { emitEvent: false } to prevent this programmatic update
        // from triggering the form.valueChanges subscription below.
        this.fields[0].formControl.setValue(catIds, {emitEvent: false});
      }
    });
  }

  ngOnInit(): void {
    this.filter.setViewRef(this);

    this.model = {
      categories: this.filter.selectedValues(), // Synchronously get the initial signal value
    };

    this.fields = [
      {
        key: 'categories',
        type: CustomFieldTypes.CATEGORY_TREE,
        props: {
          categoryDataSignal: this.filter.catTree,
        },
      },
    ];

    this.form.valueChanges.subscribe(value => {
      const selectedCategories = value['categories'] ?? [];

      // Only load if the filter is ready OR if a non-empty selection is made, indicating user interaction
      if (this.filter.isReady() || selectedCategories.length > 0) {
        this.filter.load(selectedCategories);
      }
    });
  }

  // Forces sync of the view's internal Formly control
  // Example usage: search bar component when a filter chip is removed
  forceSyncUI(): void {
    const catIds = this.filter?.selectedValues() ?? [];
    if (Array.isArray(catIds) && this.fields?.[0]?.formControl) {
      this.fields[0].formControl.setValue(catIds);
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class CategoryFilter implements Filter<'Resources'> {
  key = 'category';
  label = 'Category';
  component = CategoryView;
  selectedValues = signal<string[]>([]);
  selected: Signal<FilterSelection[]>;
  defaultExpanded: boolean = true;
  load(values: string[]): void {
    this.selectedValues.set(values);
  }
  apply(filterBody: ResourceSearchSchema): void {
    const selected = this.selectedValues();
    if (selected.length == 0) return;
    filterBody.category = {
      terms: selected,
      match_all: false,
    };
  }
  add(value: string): void {
    this.selectedValues.update(values => [...values, value]);
  }
  remove(value: string): void {
    const previousLength = this.selectedValues().length;
    this.selectedValues.update(values => values.filter(v => v != value));

    // Explicitly call forceSyncUI to reset the CategorySelectTree checkboxes
    // when a single chip is removed. The template only calls filter.remove(),
    // so the logic to sync the view must live here.
    if (this.selectedValues().length < previousLength) {
      this.forceSyncUI();
    }
  }
  showCategoryButton = true;
  visibility = FilterVisibility.EVERYONE;

  // Track when category data is loaded and the filter is initialized
  isReady = signal(false);

  catTree: Signal<(PrimaryCategory & {children: (SecondaryCategory & {children: TertiaryCategory[]})[]})[]>;

  // Hold the reference to the CategoryView component instance
  private categoryViewRef: CategoryView | null = null; // Ignore SonarQube, can't be `readonly`

  public setViewRef(view: CategoryView): void {
    this.categoryViewRef = view;
  }

  // Calls the sync method on the view component
  forceSyncUI(): void {
    this.categoryViewRef?.forceSyncUI();
  }

  constructor(categoryService: CategoryService) {
    const obsCatTree = <Observable<PrimaryCategoryChildrenListResponse>>(
      categoryService.getCategoriesByLevel('primary', {hierarchy: 'children'})
    );
    const obsCatsFlat = obsCatTree.pipe(
      map(tree => {
        const cats = [];
        for (let pcat of tree.primary_categories) {
          cats.push({name: pcat.name, id: pcat.id});
          for (let scat of pcat.secondary_categories) {
            cats.push({name: `${pcat.name} \u203A ${scat.name}`, id: scat.id});
            for (let tcat of scat.tertiary_categories) {
              cats.push({name: `${pcat.name} \u203A ${scat.name} \u203A ${tcat.name}`, id: tcat.id});
            }
          }
        }
        return cats;
      }),
      tap(() => this.isReady.set(true)), // Set ready flag after data is available
    );
    const obsCatsHierarchy = obsCatTree.pipe(map(addChildrenToCats));

    this.catTree = toSignal(obsCatsHierarchy) as Signal<any>;

    const sigCats = toSignal(obsCatsFlat, {initialValue: []});
    this.selected = computed(() => {
      const selected = this.selectedValues();
      const cats = sigCats();
      if (cats.length == 0) {
        return selected.map(value => ({label: value, value}));
      }
      return selected.map(value => ({value, label: cats.find(cat => cat.id == value)?.name ?? value}));
    });
  }
}
